<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import UazapiAPI from 'dashboard/api/uazapi';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConnectionPanel from 'dashboard/components/uazapi/ConnectionPanel.vue';

const props = defineProps({ inbox: { type: Object, required: true } });
const { t } = useI18n();
const store = useStore();

const linked = computed(() => Boolean(props.inbox.uazapi));
const isSuperAdmin = computed(
  () => store.getters.getCurrentUser?.type === 'SuperAdmin'
);

const status = ref(props.inbox.uazapi?.status || 'disconnected');
const phone = ref(props.inbox.uazapi?.phone_number || '');
const showPanel = ref(false);
const instances = ref([]);
const adopting = ref('');

const statusLabel = computed(() =>
  t(`INBOX_MGMT.UAZAPI.CONNECTION.STATUS_${status.value}`)
);

const errorOf = e => e?.response?.data?.error || e.message;

const refresh = async () => {
  if (!linked.value) return;
  try {
    const { data } = await UazapiAPI.status(props.inbox.id);
    status.value = data.status;
    phone.value = data.phone_number || phone.value;
  } catch (e) {
    useAlert(errorOf(e));
  }
};

const reconnect = () => {
  showPanel.value = true;
};

const onConnected = data => {
  status.value = 'connected';
  phone.value = data.phone_number || phone.value;
  showPanel.value = false;
  store.dispatch('inboxes/get');
};

const disconnect = async () => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('INBOX_MGMT.UAZAPI.CONNECTION.DISCONNECT_CONFIRM')))
    return;
  try {
    await UazapiAPI.disconnect(props.inbox.id);
    status.value = 'disconnected';
    store.dispatch('inboxes/get');
  } catch (e) {
    useAlert(errorOf(e));
  }
};

const loadInstances = async () => {
  if (!isSuperAdmin.value || linked.value) return;
  try {
    const { data } = await UazapiAPI.instances();
    instances.value = data.payload;
  } catch (e) {
    useAlert(errorOf(e));
  }
};

const adopt = async instanceId => {
  adopting.value = instanceId;
  try {
    await UazapiAPI.adopt(props.inbox.id, instanceId);
    useAlert(t('INBOX_MGMT.UAZAPI.CONNECTION.ADOPT_DONE'));
    await store.dispatch('inboxes/get');
  } catch (e) {
    useAlert(errorOf(e));
  } finally {
    adopting.value = '';
  }
};

onMounted(() => {
  refresh();
  loadInstances();
});
</script>

<template>
  <div class="mx-6 max-w-4xl flex flex-col gap-6">
    <template v-if="linked">
      <dl class="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
        <dt class="text-n-slate-11">
          {{ t('INBOX_MGMT.UAZAPI.CONNECTION.STATUS') }}
        </dt>
        <dd
          :class="status === 'connected' ? 'text-n-teal-11' : 'text-n-ruby-11'"
        >
          {{ statusLabel }}
        </dd>
        <dt class="text-n-slate-11">
          {{ t('INBOX_MGMT.UAZAPI.CONNECTION.PHONE') }}
        </dt>
        <dd>{{ phone || '-' }}</dd>
        <dt class="text-n-slate-11">
          {{ t('INBOX_MGMT.UAZAPI.CONNECTION.INSTANCE') }}
        </dt>
        <dd>{{ inbox.uazapi.instance_name }}</dd>
      </dl>
      <div class="flex gap-2">
        <NextButton
          :label="t('INBOX_MGMT.UAZAPI.CONNECTION.RECONNECT')"
          @click="reconnect"
        />
        <NextButton
          v-if="status === 'connected'"
          :label="t('INBOX_MGMT.UAZAPI.CONNECTION.DISCONNECT')"
          faded
          ruby
          @click="disconnect"
        />
      </div>
      <ConnectionPanel
        v-if="showPanel"
        :inbox-id="inbox.id"
        @connected="onConnected"
      />
    </template>
    <template v-else>
      <p class="text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.UAZAPI.CONNECTION.NOT_LINKED') }}
      </p>
      <div v-if="isSuperAdmin" class="flex flex-col gap-3">
        <h3 class="text-base font-medium">
          {{ t('INBOX_MGMT.UAZAPI.CONNECTION.ADOPT_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.UAZAPI.CONNECTION.ADOPT_DESC') }}
        </p>
        <table class="text-sm">
          <tbody>
            <tr
              v-for="item in instances"
              :key="item.id"
              class="border-b border-n-weak"
            >
              <td class="py-2 pr-4 font-medium">{{ item.name }}</td>
              <td class="py-2 pr-4">{{ item.phone_number || '-' }}</td>
              <td class="py-2 pr-4">
                {{ t(`INBOX_MGMT.UAZAPI.CONNECTION.STATUS_${item.status}`) }}
              </td>
              <td class="py-2">
                <span v-if="item.linked" class="text-n-slate-11">{{
                  t('INBOX_MGMT.UAZAPI.CONNECTION.ADOPT_LINKED')
                }}</span>
                <NextButton
                  v-else
                  sm
                  :is-loading="adopting === item.id"
                  :label="t('INBOX_MGMT.UAZAPI.CONNECTION.ADOPT_BUTTON')"
                  @click="adopt(item.id)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>
